import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "acme-solver" }
const _hoisted_2 = { class: "row mmb-5" }
const _hoisted_3 = { class: "col span-6" }
const _hoisted_4 = { class: "mmt-5" }
const _hoisted_5 = { class: "row" }
const _hoisted_6 = { class: "col span-6" }
const _hoisted_7 = { class: "col span-6" }

import { computed } from 'vue';
import { useStore } from 'vuex';
import ArrayList from '@shell/components/form/ArrayList';
import KeyValue from '@shell/components/form/KeyValue';
import Banner from '@components/Banner/Banner.vue';
import { LabeledInput } from '@components/Form/LabeledInput';
import { _EDIT } from '@shell/config/query-params';
import { useI18n } from '@shell/composables/useI18n';
import { CHALLENGE_TYPES, HTTP01_INGRESS_MODES } from '../../form-options';
import { RadioGroup } from '@components/Form/Radio';
import type { AcmeSolver } from '../../schema';
import Dns01Provider from './Dns01Provider.vue';

type IngressMode = typeof HTTP01_INGRESS_MODES[number];

interface Props {
  /** A single entry of `spec.acme.solvers`, bound into directly. */
  value: AcmeSolver;
  mode?: string;
  /** Position of this solver in the list. Used to give the radio group a name that is unique across the page. */
  index: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AcmeSolver',
  props: {
    value: {},
    mode: { default: _EDIT },
    index: {}
  },
  setup(__props: any, { expose: __expose }) {

const props = __props;

const store = useStore();
const { t } = useI18n(store);

const radioName = computed(() => `challengeType-${ props.index }`);

props.value.selector = props.value.selector || {};

if (!props.value.http01 && !props.value.dns01) {
  props.value.http01 = { ingress: {} };
}

const selector = computed(() => props.value.selector as NonNullable<AcmeSolver['selector']>);
const dns01Config = computed(() => props.value.dns01 as Record<string, any>);
const hasIngress = computed(() => !!props.value.http01?.ingress);
const gatewayHttpRoute = computed(() => props.value.http01?.gatewayHTTPRoute);

const challengeTypeOptions = computed(() => [CHALLENGE_TYPES.HTTP01, CHALLENGE_TYPES.DNS01].map((value) => ({
  value,
  label:       t(`certManager.solver.${ value }`),
  // Raw - `RadioButton` interpolates the description as text, so an escaped apostrophe
  // would reach the page as `&#39;`.
  description: t(`certManager.solver.${ value }Description`, undefined, true),
})));

const challengeType = computed<string>({
  get() {
    return props.value.dns01 ? CHALLENGE_TYPES.DNS01 : CHALLENGE_TYPES.HTTP01;
  },
  set(neu) {
    // The two are mutually exclusive - cert-manager rejects a solver with both.
    if (neu === CHALLENGE_TYPES.DNS01) {
      delete props.value.http01;
      props.value.dns01 = props.value.dns01 || {};
    } else {
      delete props.value.dns01;
      props.value.http01 = props.value.http01 || { ingress: {} };
    }
  },
});

const ingressModeOptions = computed(() => HTTP01_INGRESS_MODES.map((value) => ({
  value,
  label:       t(`certManager.solver.ingressMode.${ value }`),
  description: t(`certManager.solver.ingressMode.${ value }Description`, undefined, true),
})));

/**
 * cert-manager allows exactly one of `ingressClassName`, `class` or `name` on an HTTP-01
 * ingress solver and the webhook rejects any combination, so this is a choice rather than
 * three fields. Derived from whichever key is set, so YAML-authored solvers round-trip.
 */
const ingressMode = computed<IngressMode>({
  get() {
    const ingress = props.value.http01?.ingress || {};

    return HTTP01_INGRESS_MODES.find((mode) => ingress[mode] !== undefined) || HTTP01_INGRESS_MODES[0];
  },
  set(neu) {
    const ingress = props.value.http01?.ingress || {};

    HTTP01_INGRESS_MODES.forEach((mode) => delete ingress[mode]);
    ingress[neu] = '';
    props.value.http01!.ingress = ingress;
  },
});

const ingressValue = computed<string | undefined>({
  get() {
    return props.value.http01?.ingress?.[ingressMode.value];
  },
  set(neu) {
    if (props.value.http01?.ingress) {
      props.value.http01.ingress[ingressMode.value] = neu;
    }
  },
});

const isCatchAll = computed(() => {
  const { dnsZones = [], dnsNames = [], matchLabels = {} } = props.value.selector || {};

  return !dnsZones.length && !dnsNames.length && !Object.keys(matchLabels).length;
});

__expose({
  radioName, challengeType, challengeTypeOptions, ingressMode, ingressModeOptions, isCatchAll
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(RadioGroup), {
      value: challengeType.value,
      "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((challengeType).value = $event)),
      name: radioName.value,
      label: _unref(t)('certManager.solver.challengeType'),
      options: challengeTypeOptions.value,
      mode: __props.mode,
      class: "mmb-5"
    }, null, 8, ["value", "name", "label", "options", "mode"]),
    _cache[9] || (_cache[9] = _createTextVNode()),
    (challengeType.value === 'http01')
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          (hasIngress.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_unref(RadioGroup), {
                  value: ingressMode.value,
                  "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((ingressMode).value = $event)),
                  name: `ingressMode-${ radioName.value }`,
                  label: _unref(t)('certManager.solver.ingressMode.label'),
                  options: ingressModeOptions.value,
                  mode: __props.mode,
                  class: "mmb-5"
                }, null, 8, ["value", "name", "label", "options", "mode"]),
                _cache[6] || (_cache[6] = _createTextVNode()),
                _createElementVNode("div", _hoisted_2, [
                  _createElementVNode("div", _hoisted_3, [
                    _createVNode(_unref(LabeledInput), {
                      value: ingressValue.value,
                      "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((ingressValue).value = $event)),
                      label: _unref(t)(`certManager.solver.ingressMode.${ ingressMode.value }`),
                      mode: __props.mode
                    }, null, 8, ["value", "label", "mode"])
                  ])
                ])
              ], 64))
            : _createCommentVNode("", true),
          _cache[7] || (_cache[7] = _createTextVNode()),
          (gatewayHttpRoute.value)
            ? (_openBlock(), _createBlock(Banner, {
                key: 1,
                color: "info",
                label: _unref(t)('certManager.solver.gatewayHttpRoute')
              }, null, 8, ["label"]))
            : _createCommentVNode("", true)
        ], 64))
      : (_openBlock(), _createBlock(Dns01Provider, {
          key: 1,
          value: dns01Config.value,
          mode: __props.mode
        }, null, 8, ["value", "mode"])),
    _cache[10] || (_cache[10] = _createTextVNode()),
    _createElementVNode("h3", _hoisted_4, _toDisplayString(_unref(t)('certManager.solver.selector')), 1),
    _cache[11] || (_cache[11] = _createTextVNode()),
    (isCatchAll.value)
      ? (_openBlock(), _createBlock(Banner, {
          key: 2,
          color: "info",
          label: _unref(t)('certManager.solver.catchAllHelp')
        }, null, 8, ["label"]))
      : _createCommentVNode("", true),
    _cache[12] || (_cache[12] = _createTextVNode()),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createVNode(_unref(ArrayList), {
          value: selector.value.dnsZones,
          "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((selector.value.dnsZones) = $event)),
          title: _unref(t)('certManager.solver.dnsZones'),
          "add-label": _unref(t)('certManager.solver.addDnsZone'),
          mode: __props.mode
        }, null, 8, ["value", "title", "add-label", "mode"])
      ]),
      _cache[8] || (_cache[8] = _createTextVNode()),
      _createElementVNode("div", _hoisted_7, [
        _createVNode(_unref(ArrayList), {
          value: selector.value.dnsNames,
          "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((selector.value.dnsNames) = $event)),
          title: _unref(t)('certManager.solver.dnsNames'),
          "add-label": _unref(t)('certManager.solver.addDnsName'),
          mode: __props.mode
        }, null, 8, ["value", "title", "add-label", "mode"])
      ])
    ]),
    _cache[13] || (_cache[13] = _createTextVNode()),
    _createVNode(_unref(KeyValue), {
      value: selector.value.matchLabels,
      "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((selector.value.matchLabels) = $event)),
      title: _unref(t)('certManager.solver.matchLabels'),
      mode: __props.mode,
      "read-allowed": false,
      class: "mmt-5"
    }, null, 8, ["value", "title", "mode"])
  ]))
}
}

})